let cachedApiKey: string | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Utility function to extract JSON from markdown code blocks
const extractJsonFromMarkdown = (text: string): string => {
  console.log('🔧 Extracting JSON from response:', text.substring(0, 200) + '...');

  // Try to find JSON wrapped in markdown code blocks
  const jsonBlockMatch = text.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
  if (jsonBlockMatch && jsonBlockMatch[1]) {
    console.log('✅ Found JSON in markdown block');
    return jsonBlockMatch[1].trim();
  }

  // Try to find JSON starting with { and ending with }
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (jsonMatch && jsonMatch[0]) {
    console.log('✅ Found JSON pattern');
    return jsonMatch[0].trim();
  }

  // If no JSON pattern found, return original text
  console.log('⚠️ No JSON pattern found, returning original text');
  return text.trim();
};

const getApiKey = async (): Promise<string> => {
  // Check if cached key is still valid (within cache duration)
  const now = Date.now();
  if (cachedApiKey && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
    return cachedApiKey;
  }

  try {
    // Always fetch fresh API key from database
    if (!window.ezsite?.apis?.tablePage) {
      throw new Error('Database API not available. Please refresh the page and try again.');
    }

    const response = await window.ezsite.apis.tablePage('15215', {
      PageNo: 1,
      PageSize: 1,
      Filters: [
      {
        name: 'setting_key',
        op: 'Equal',
        value: 'gemini_api_key'
      }]

    });

    if (response.error) {
      console.error('Error fetching API key from database:', response.error);
      throw new Error('Failed to fetch API key from database. Please contact your administrator.');
    }

    if (response.data?.List && response.data.List.length > 0) {
      const setting = response.data.List[0];
      if (setting.setting_value && setting.setting_value.trim()) {
        cachedApiKey = setting.setting_value.trim();
        cacheTimestamp = now;
        console.log('✅ API key successfully loaded from database');
        return cachedApiKey;
      } else {
        throw new Error('Gemini API key is configured but empty. Please ask your administrator to set a valid API key.');
      }
    } else {
      throw new Error('Gemini API key not configured. Please contact your administrator to set up the API key.');
    }
  } catch (error) {
    console.error('Error getting API key:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to retrieve API key. Please contact your administrator.');
  }
};

// Clear cache when API key is updated
export const clearApiKeyCache = () => {
  cachedApiKey = null;
  cacheTimestamp = null;
  console.log('🗑️ API key cache cleared');
};

// Update API key in cache
export const updateApiKeyCache = (apiKey: string) => {
  cachedApiKey = apiKey.trim();
  cacheTimestamp = Date.now();
  console.log('🔄 API key cache updated');
};

const geminiService = async (prompt: string, options?: {
  model?: string;
  temperature?: number;
  maxTokens?: number;
}) => {
  try {
    const apiKey = await getApiKey();

    if (!apiKey) {
      throw new Error('Gemini API key not configured');
    }

    const model = options?.model || 'gemini-1.5-flash';
    const temperature = options?.temperature || 0.7;
    const maxTokens = options?.maxTokens || 1000;

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contents: [
          {
            parts: [
            {
              text: prompt
            }]

          }],

          generationConfig: {
            temperature,
            maxOutputTokens: maxTokens,
            topP: 0.8,
            topK: 10
          }
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Gemini API Error:', errorData);

      if (response.status === 400) {
        // Check if it's a model not found error
        if (errorData?.error?.message?.includes('model not found')) {
          throw new Error('Model not available. The API key may not have access to this model.');
        }
        throw new Error('Invalid request to Gemini API. Please check your input.');
      } else if (response.status === 401) {
        throw new Error('Invalid API key. Please contact your administrator.');
      } else if (response.status === 403) {
        throw new Error('API access forbidden. Please check your quota or permissions.');
      } else if (response.status === 429) {
        throw new Error('API rate limit exceeded. Please try again later.');
      } else if (response.status === 404) {
        throw new Error('Model not found. Please check if the API key has access to the required model.');
      } else {
        throw new Error(`Gemini API error: ${response.status}`);
      }
    }

    const data = await response.json();

    if (data.candidates && data.candidates[0] && data.candidates[0].content) {
      return data.candidates[0].content.parts[0].text;
    } else {
      console.error('Unexpected response format:', data);
      throw new Error('Unexpected response format from Gemini API');
    }
  } catch (error) {
    console.error('Gemini Service Error:', error);

    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error('An unexpected error occurred while calling Gemini API');
    }
  }
};

// Enhanced image analysis function
geminiService.analyzeImage = async (
imageBase64: string,
mimeType: string,
prompt: string = 'Please analyze this image and describe what you see in detail.') =>
{
  try {
    const apiKey = await getApiKey();

    if (!apiKey) {
      throw new Error('Gemini API key not configured');
    }

    const model = 'gemini-1.5-flash';

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contents: [
          {
            parts: [
            {
              text: prompt
            },
            {
              inline_data: {
                mime_type: mimeType,
                data: imageBase64
              }
            }]

          }],

          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 1000,
            topP: 0.8,
            topK: 10
          }
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Gemini Vision API Error:', errorData);

      if (response.status === 400) {
        if (errorData?.error?.message?.includes('model not found')) {
          throw new Error('Vision model not available. The API key may not have access to this model.');
        }
        throw new Error('Invalid image or request. Please check your image format.');
      } else if (response.status === 401) {
        throw new Error('Invalid API key. Please contact your administrator.');
      } else if (response.status === 403) {
        throw new Error('API access forbidden. Please check your quota or permissions.');
      } else if (response.status === 429) {
        throw new Error('API rate limit exceeded. Please try again later.');
      } else {
        throw new Error(`Gemini Vision API error: ${response.status}`);
      }
    }

    const data = await response.json();

    if (data.candidates && data.candidates[0] && data.candidates[0].content) {
      return {
        text: data.candidates[0].content.parts[0].text,
        error: null
      };
    } else {
      console.error('Unexpected response format:', data);
      throw new Error('Unexpected response format from Gemini Vision API');
    }
  } catch (error) {
    console.error('Gemini Image Analysis Error:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Failed to analyze image'
    };
  }
};

// Enhanced Chatbot Response with better context handling
geminiService.getChatResponse = async (
message: string,
context: string = '',
userRole: string = 'teacher') =>
{
  try {
    const roleContext = userRole === 'teacher' ?
    'You are an AI assistant helping teachers with educational tasks, lesson planning, and classroom management. Be helpful, professional, and educational in your responses.' :
    'You are an AI assistant helping students with their studies and learning. Be encouraging, clear, and educational in your responses.';

    const prompt = `${roleContext}

${context ? `Context from previous conversation: ${context}\n\n` : ''}User: ${message}

Please provide a helpful, informative response.`;

    const text = await geminiService(prompt, {
      model: 'gemini-1.5-flash',
      temperature: 0.8,
      maxTokens: 500
    });

    return { text, error: null };
  } catch (error) {
    console.error('Error getting chat response:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Failed to get response'
    };
  }
};

// Quiz generation function with improved JSON parsing
geminiService.generateQuiz = async (
topic: string,
numQuestions: number,
difficulty: 'easy' | 'medium' | 'hard',
questionTypes: string[],
additionalInstructions: string = '') =>
{
  try {
    console.log('🎯 Generating quiz with Gemini AI...');
    console.log('Topic:', topic);
    console.log('Number of questions:', numQuestions);
    console.log('Difficulty:', difficulty);
    console.log('Question types:', questionTypes);

    const difficultyDescription = {
      easy: 'basic understanding, simple recall questions',
      medium: 'moderate complexity, application and analysis',
      hard: 'advanced concepts, critical thinking and synthesis'
    };

    const typeInstructions = questionTypes.map((type) => {
      switch (type) {
        case 'multiple_choice':
          return 'Multiple choice questions with 4 options (A, B, C, D)';
        case 'true_false':
          return 'True/False questions';
        case 'short_answer':
          return 'Short answer questions requiring 1-2 sentence responses';
        case 'essay':
          return 'Essay questions requiring detailed explanations';
        default:
          return type;
      }
    }).join(', ');

    const prompt = `Generate ${numQuestions} quiz questions about "${topic}" with ${difficulty} difficulty level (${difficultyDescription[difficulty]}).

Question types to include: ${typeInstructions}

${additionalInstructions}

IMPORTANT: Return ONLY valid JSON in the following format:
{
  "questions": [
    {
      "question": "Question text here",
      "type": "multiple_choice|true_false|short_answer|essay",
      "options": ["Option A", "Option B", "Option C", "Option D"],
      "correctAnswer": "Correct answer here",
      "explanation": "Brief explanation of the answer"
    }
  ]
}

Rules:
1. For multiple_choice: Include 4 options in the "options" array, correctAnswer should be the full text of the correct option
2. For true_false: Don't include options array, correctAnswer should be "True" or "False"
3. For short_answer/essay: Don't include options array, correctAnswer should be a sample correct answer
4. Each question should be clear, educational, and appropriate for the difficulty level
5. Ensure variety in question types if multiple types are requested
6. Make sure all JSON is properly formatted and valid

Generate exactly ${numQuestions} questions.`;

    const text = await geminiService(prompt, {
      model: 'gemini-1.5-flash',
      temperature: 0.7,
      maxTokens: 2000
    });

    console.log('✅ Quiz generated successfully');
    console.log('Raw response length:', text.length);

    return { text, error: null };
  } catch (error) {
    console.error('❌ Error generating quiz:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Failed to generate quiz'
    };
  }
};

// Generate slides function with improved JSON parsing
geminiService.generateSlides = async (
topic: string,
subject: string,
gradeLevel: string,
numberOfSlides: number,
additionalNotes: string = '') =>
{
  try {
    console.log('🎨 Generating slides with Gemini AI...');

    const prompt = `Create a comprehensive slide presentation on "${topic}" for ${subject} at ${gradeLevel} grade level.

Requirements:
- Generate exactly ${numberOfSlides} slides
- Make content age-appropriate for ${gradeLevel} level
- Include clear, engaging content suitable for educational presentations
- Focus on key concepts and learning objectives

${additionalNotes ? `Additional instructions: ${additionalNotes}` : ''}

IMPORTANT: Return ONLY valid JSON in the following format:
{
  "title": "Presentation Title",
  "slides": [
    {
      "id": "slide-1",
      "type": "title",
      "title": "Slide Title",
      "content": "Main content or subtitle",
      "layout": "title-slide"
    },
    {
      "id": "slide-2", 
      "type": "content",
      "title": "Slide Title",
      "content": ["Bullet point 1", "Bullet point 2", "Bullet point 3"],
      "layout": "bullet-points"
    }
  ]
}

Slide types to use:
- "title" for introduction/title slides
- "content" for main content slides
- "conclusion" for summary/conclusion slides

Layouts to use:
- "title-slide" for title slides
- "bullet-points" for lists
- "text-image" for content with descriptions
- "two-column" for comparisons

Generate exactly ${numberOfSlides} slides with educational, engaging content.`;

    const text = await geminiService(prompt, {
      model: 'gemini-1.5-flash',
      temperature: 0.7,
      maxTokens: 3000
    });

    console.log('✅ Slides generated successfully');
    return { text, error: null };
  } catch (error) {
    console.error('❌ Error generating slides:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Failed to generate slides'
    };
  }
};

// Generate content function
geminiService.generateContent = async (
prompt: string,
contentType: string = 'educational',
options: any = {}) =>
{
  try {
    console.log('📝 Generating content with Gemini AI...');

    const enhancedPrompt = `Generate ${contentType} content based on the following request:

${prompt}

Please provide comprehensive, well-structured content that is:
- Educational and informative
- Age-appropriate if specified
- Well-organized with clear structure
- Engaging and easy to understand

${options.additionalInstructions ? `Additional requirements: ${options.additionalInstructions}` : ''}

Return the content in a clear, structured format.`;

    const text = await geminiService(enhancedPrompt, {
      model: 'gemini-1.5-flash',
      temperature: options.temperature || 0.7,
      maxTokens: options.maxTokens || 2000
    });

    console.log('✅ Content generated successfully');
    return { text, error: null };
  } catch (error) {
    console.error('❌ Error generating content:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Failed to generate content'
    };
  }
};

// Parse JSON with improved error handling
geminiService.parseJsonResponse = (text: string) => {
  try {
    console.log('🔧 Parsing JSON response...');

    // Extract JSON from markdown if needed
    const cleanedText = extractJsonFromMarkdown(text);

    // Parse the JSON
    const parsed = JSON.parse(cleanedText);
    console.log('✅ JSON parsed successfully');
    return { data: parsed, error: null };
  } catch (parseError) {
    console.error('❌ Failed to parse JSON:', parseError);
    console.log('Raw text:', text.substring(0, 500) + '...');
    return {
      data: null,
      error: 'Invalid response format from AI service. Please try again.'
    };
  }
};

// Analyze YouTube link function
geminiService.analyzeYouTubeLink = async (
url: string,
analysisType: string = 'educational') =>
{
  try {
    console.log('🎥 Analyzing YouTube content...');

    const prompt = `Please analyze this YouTube video URL for ${analysisType} purposes: ${url}

Based on the URL and any available information, provide:
1. Likely topic/subject matter
2. Educational value assessment
3. Suggested grade level appropriateness
4. Key learning objectives that could be derived
5. How this could be integrated into lesson planning

If you cannot access the actual video content, provide analysis based on the URL structure, title, and channel information that may be visible.`;

    const text = await geminiService(prompt, {
      model: 'gemini-1.5-flash',
      temperature: 0.6,
      maxTokens: 1000
    });

    console.log('✅ YouTube content analyzed successfully');
    return { text, error: null };
  } catch (error) {
    console.error('❌ Error analyzing YouTube content:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Failed to analyze YouTube content'
    };
  }
};

// Analyze web link function
geminiService.analyzeWebLink = async (
url: string,
analysisType: string = 'educational') =>
{
  try {
    console.log('🌐 Analyzing web content...');

    const prompt = `Please analyze this web URL for ${analysisType} purposes: ${url}

Based on the URL and domain, provide:
1. Likely content type and subject matter
2. Educational value and reliability assessment
3. Suggested use cases in educational settings
4. Grade level appropriateness
5. Key topics that might be covered
6. How this resource could be integrated into curriculum

Provide analysis based on the URL structure, domain authority, and any recognizable patterns.`;

    const text = await geminiService(prompt, {
      model: 'gemini-1.5-flash',
      temperature: 0.6,
      maxTokens: 1000
    });

    console.log('✅ Web content analyzed successfully');
    return { text, error: null };
  } catch (error) {
    console.error('❌ Error analyzing web content:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Failed to analyze web content'
    };
  }
};

// Analyze uploaded file function
geminiService.analyzeUploadedFile = async (
fileName: string,
fileType: string,
content?: string,
analysisType: string = 'educational') =>
{
  try {
    console.log('📄 Analyzing uploaded file...');

    const prompt = `Please analyze this uploaded file for ${analysisType} purposes:

File Name: ${fileName}
File Type: ${fileType}
${content ? `Content Preview: ${content.substring(0, 1000)}...` : ''}

Based on the file information and content, provide:
1. Content analysis and key topics
2. Educational value assessment
3. Suggested grade level appropriateness
4. Potential learning objectives
5. How this could be used in lesson planning
6. Key concepts that could be extracted for quiz generation

Provide comprehensive analysis to help educators understand and utilize this resource effectively.`;

    const text = await geminiService(prompt, {
      model: 'gemini-1.5-flash',
      temperature: 0.6,
      maxTokens: 1500
    });

    console.log('✅ File content analyzed successfully');
    return { text, error: null };
  } catch (error) {
    console.error('❌ Error analyzing file content:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Failed to analyze file content'
    };
  }
};

// Generate lesson plan function
geminiService.generateLessonPlan = async (
topic: string,
subject: string,
gradeLevel: string,
duration: number,
objectives: string = '',
additionalNotes: string = '') =>
{
  try {
    console.log('📚 Generating lesson plan with Gemini AI...');

    const prompt = `Create a comprehensive lesson plan with the following specifications:

Topic: ${topic}
Subject: ${subject}
Grade Level: ${gradeLevel}
Duration: ${duration} minutes
${objectives ? `Learning Objectives: ${objectives}` : ''}
${additionalNotes ? `Additional Notes: ${additionalNotes}` : ''}

Please create a detailed lesson plan including:

1. LESSON OVERVIEW
   - Title
   - Subject and Grade Level
   - Duration
   - Learning Objectives (clear, measurable goals)

2. MATERIALS NEEDED
   - List of required materials and resources

3. LESSON STRUCTURE
   - Opening/Hook (5-10 minutes)
   - Introduction (10-15% of total time)
   - Main Activity/Instruction (60-70% of total time)
   - Practice/Application (15-20% of total time)
   - Closure/Summary (5-10 minutes)

4. DETAILED ACTIVITIES
   - Step-by-step instructions for each phase
   - Teacher actions and student expectations
   - Timing for each activity

5. ASSESSMENT METHODS
   - Formative assessment strategies
   - Summative assessment ideas
   - Success criteria

6. DIFFERENTIATION STRATEGIES
   - Accommodations for different learning styles
   - Support for struggling learners
   - Extensions for advanced students

7. HOMEWORK/FOLLOW-UP
   - Suggested homework assignments
   - Next lesson connections

Make the lesson plan engaging, age-appropriate, and aligned with educational best practices.`;

    const text = await geminiService(prompt, {
      model: 'gemini-1.5-flash',
      temperature: 0.7,
      maxTokens: 3000
    });

    console.log('✅ Lesson plan generated successfully');
    return { text, error: null };
  } catch (error) {
    console.error('❌ Error generating lesson plan:', error);
    return {
      text: '',
      error: error instanceof Error ? error.message : 'Failed to generate lesson plan'
    };
  }
};

// Make the gemini service available globally for the chatbot
if (typeof window !== 'undefined') {
  window.ezsite = window.ezsite || {};
  window.ezsite.gemini = geminiService;
}

export { geminiService };
export default geminiService;